import clsx from 'clsx';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { AuditTrailEntryResponse, RoutingTraceResponse, RouteAttemptResponse } from '../api';
import {
  EVENT_FAMILY,
  EVENT_LABEL,
  FAMILY_COLOR,
  collapseConsecutive,
  getEventIcon,
  isStartedEvent,
  type CollapsedEntry,
  type DetailChip,
} from './auditTrailMeta';

interface AuditTrailPanelProps {
  open: boolean;
  onClose: () => void;
  entries: ReadonlyArray<AuditTrailEntryResponse>;
  /** Fase 8 preparation cascade. Null for legacy jobs. */
  routingTrace?: RoutingTraceResponse | null;
  isLoading?: boolean;
}

/**
 * Human-friendly label for each preparation route. Kept in sync with PreparationRoute (Domain).
 * R1 local · R2 sidecar+DI · R3 sidecar+CU · R4 DI legacy · R5 CU nativo · R6 LLM vision (stub).
 */
const ROUTE_LABELS: Record<string, string> = {
  R1: 'R1 · MarkItDown local',
  R2: 'R2 · Sidecar + Document Intelligence',
  R3: 'R3 · Sidecar + Content Understanding',
  R4: 'R4 · DI legacy (fallback)',
  R5: 'R5 · CU nativo (.NET)',
  R6: 'R6 · LLM Vision (deshabilitada)',
};

const OUTCOME_STYLE: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-700',
  fallback: 'bg-amber-100 text-amber-700',
  timeout: 'bg-red-100 text-red-700',
  circuit_open: 'bg-red-100 text-red-700',
  unsupported: 'bg-slate-200 text-slate-700',
  error: 'bg-red-100 text-red-700',
};

function RouteAttemptRow({ attempt, index }: { attempt: RouteAttemptResponse; index: number }): JSX.Element {
  const outcomeClass = OUTCOME_STYLE[attempt.outcome] ?? 'bg-slate-100 text-slate-700';
  return (
    <li className="rounded border border-slate-200 bg-slate-50 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-slate-500">#{index + 1}</span>
        <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', outcomeClass)}>
          {attempt.outcome}
        </span>
      </div>
      <div className="mt-1 text-sm font-medium text-slate-800">
        {ROUTE_LABELS[attempt.route] ?? attempt.route}
      </div>
      <div className="mt-0.5 text-xs text-slate-500">{attempt.latencyMs} ms</div>
      {attempt.dropReason ? (
        <div className="mt-1 text-xs text-amber-700">
          <span className="font-semibold">Drop:</span> {attempt.dropReason}
        </div>
      ) : null}
      {attempt.errorCode ? (
        <div className="mt-1 text-xs text-red-700">
          <span className="font-semibold">Error:</span> {attempt.errorCode}
        </div>
      ) : null}
    </li>
  );
}

function ChipRow({ chips, familyChipTone }: { chips: DetailChip[]; familyChipTone: string }): JSX.Element | null {
  if (chips.length === 0) {
    return null;
  }
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {chips.map((chip, i) => (
        <span
          key={`${chip.label}-${i}`}
          className={clsx(
            'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium leading-none',
            chip.tone ?? familyChipTone,
          )}
        >
          {chip.label}
        </span>
      ))}
    </div>
  );
}

function TimelineRow({ row, isLast }: { row: CollapsedEntry; isLast: boolean }): JSX.Element {
  const family = EVENT_FAMILY[row.eventType] ?? 'lifecycle';
  const palette = FAMILY_COLOR[family];
  const Icon = getEventIcon(row.eventType);
  const hollow = isStartedEvent(row.eventType);
  const label = EVENT_LABEL[row.eventType] ?? row.eventType;

  const markerClass = hollow
    ? clsx(
        'border-2 bg-white',
        palette.marker.replace(/bg-[\w-]+/, '').replace(/text-\w+/, ''),
      )
    : palette.marker;

  return (
    <li className={clsx('relative pl-10', isLast ? '' : 'pb-4')}>
      {/* marker */}
      <span
        className={clsx(
          'absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full ring-4',
          palette.ring,
          markerClass,
        )}
      >
        <Icon
          className={clsx('h-3.5 w-3.5', hollow ? 'text-slate-600' : 'text-white')}
          strokeWidth={2.25}
        />
      </span>

      {/* header */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-400">
          {new Date(row.lastAt).toLocaleTimeString()}
          {row.firstAt !== row.lastAt ? (
            <span className="ml-1 normal-case text-slate-400">
              · desde {new Date(row.firstAt).toLocaleTimeString()}
            </span>
          ) : null}
        </div>
        {typeof row.durationMs === 'number' ? (
          <span className="text-[11px] text-slate-400">{row.durationMs}ms</span>
        ) : null}
      </div>

      <div className="mt-0.5 flex items-center gap-1.5">
        <span className="text-sm font-semibold text-slate-800">{label}</span>
        {row.occurrences > 1 ? (
          <span className="inline-flex items-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
            ×{row.occurrences}
          </span>
        ) : null}
      </div>

      <ChipRow chips={row.chips} familyChipTone={palette.chip} />

      <div className="mt-1 text-[11px] text-slate-500">
        <span className="font-medium">Actor:</span> {row.actorId}
      </div>
    </li>
  );
}

/**
 * Side panel showing the audit trail of a document job.
 *
 * The public API does NOT expose the audit trail (per PHASE_4 line 158); the
 * Wave 6.A reviewer-only endpoint `GET /documents/{id}/audit-trail` does. We
 * render the chronological list of lifecycle events as a vertical timeline,
 * collapsing consecutive duplicates and surfacing engine/provider/route detail
 * chips so reviewers can tell at a glance whether a step was driven by
 * MarkItDown, Document Intelligence, an LLM or Content Understanding.
 */
export function AuditTrailPanel({
  open,
  onClose,
  entries,
  routingTrace = null,
  isLoading = false,
}: AuditTrailPanelProps): JSX.Element {
  const rows = collapseConsecutive(entries);

  return (
    <aside
      aria-hidden={!open}
      className={clsx(
        'fixed inset-y-0 right-0 z-30 flex w-full max-w-sm transform flex-col border-l border-slate-200 bg-white shadow-xl transition-transform duration-300',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Audit trail</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-500 hover:bg-slate-100"
          aria-label="Cerrar audit trail"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-5">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner label="Cargando audit trail…" />
          </div>
        ) : (
          <>
            {routingTrace ? (
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Cascada de preparación
                </h3>
                <div className="mt-2 rounded border border-primary/30 bg-primary/5 px-3 py-2">
                  <div className="text-xs text-slate-600">Ruta final</div>
                  <div className="text-sm font-semibold text-slate-900">
                    {ROUTE_LABELS[routingTrace.finalRoute] ?? routingTrace.finalRoute}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    Total {routingTrace.totalLatencyMs} ms · {routingTrace.attempts.length} intento(s)
                  </div>
                </div>
                {routingTrace.attempts.length > 0 ? (
                  <ol className="mt-2 space-y-2">
                    {routingTrace.attempts.map((attempt, index) => (
                      <RouteAttemptRow
                        key={`${attempt.route}-${index}`}
                        attempt={attempt}
                        index={index}
                      />
                    ))}
                  </ol>
                ) : null}
              </section>
            ) : null}

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Eventos de ciclo de vida
              </h3>
              {rows.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">Sin entradas en el audit trail.</p>
              ) : (
                <ol className="relative mt-3 border-l-2 border-slate-200 pl-1">
                  {rows.map((row, index) => (
                    <TimelineRow
                      key={`${row.firstAt}-${row.eventType}-${index}`}
                      row={row}
                      isLast={index === rows.length - 1}
                    />
                  ))}
                </ol>
              )}
            </section>
          </>
        )}
      </div>
    </aside>
  );
}
